import { type ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PageSizeSelect } from './PageSizeSelect';
import type { PageSize } from './use-list-params';

/** 44 px targets on the phone only; the wide layout keeps its size (#956). */
const TOUCH = '@max-[619px]/shell:min-h-11 @max-[619px]/shell:min-w-11';

/**
 * The list's numbered pager. With `summary` (#952 M6) the list's one count line takes the place of
 * «N risultati»; with `sizeChoice` (#952 M4) «Righe per pagina» sits beside it, the same on every
 * list. On a phone only the current number shows between «Precedente» and «Successiva», and the
 * buttons never wrap, so «Successiva» stays on the row (#956 follow-up).
 */
export function NumberedPager({
  page,
  pageSize,
  total,
  onPage,
  summary,
  sizeChoice,
}: {
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly onPage: (page: number) => void;
  /** The list's count line, in place of «N risultati». */
  readonly summary?: ReactNode;
  /** «Righe per pagina», beside the pager. */
  readonly sizeChoice?:
    { readonly value: PageSize; readonly onChange: (size: PageSize) => void } | undefined;
}) {
  const { t } = useTranslation('common');
  const pages = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => {
    if (page > pages) onPage(pages);
  }, [page, pages, onPage]);
  const numbers = [...new Set([1, page - 1, page, page + 1, pages])]
    .filter((n) => n >= 1 && n <= pages)
    .sort((a, b) => a - b);
  return (
    <div className="flex max-w-full min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-2 text-sm">
      {summary === undefined ? null : summary}
      <nav aria-label={t('list.pager.label')} className="flex flex-wrap items-center gap-2">
        {summary === undefined ? (
          <span className="@max-[619px]/shell:basis-full">
            {t('list.pager.total', { count: total })}
          </span>
        ) : null}
        <span className="flex flex-nowrap items-center gap-2">
          <Button
            variant="outline"
            className={TOUCH}
            disabled={page <= 1}
            onClick={() => onPage(page - 1)}
          >
            {t('list.pager.previous')}
          </Button>
          {numbers.map((n, i) => (
            <span key={n} className={cn(n !== page && '@max-[619px]/shell:hidden')}>
              {i > 0 && n - numbers[i - 1]! > 1 ? <span aria-hidden="true">… </span> : null}
              <Button
                variant="outline"
                className={cn(
                  TOUCH,
                  n === page &&
                    'border-selected-border bg-selected text-selected-foreground hover:bg-selected',
                )}
                aria-current={n === page ? 'page' : undefined}
                onClick={() => onPage(n)}
              >
                {n}
              </Button>
            </span>
          ))}
          <Button
            variant="outline"
            className={TOUCH}
            disabled={page >= pages}
            onClick={() => onPage(page + 1)}
          >
            {t('list.pager.next')}
          </Button>
        </span>
      </nav>
      {sizeChoice ? (
        <PageSizeSelect value={sizeChoice.value} onChange={sizeChoice.onChange} />
      ) : null}
    </div>
  );
}
